import {Component, Input}             from '@angular/core';
import {DomSanitizer, SafeHtml}                                     from '@angular/platform-browser';
import {GenericChartConfig, GenericXAxisConfig, GenericYAxisConfig} from './ap-generic-chart.types';
import {ChartComponent, LegendItemClickEvent, SeriesHoverEvent}     from '@progress/kendo-angular-charts';
import {StatisticStore} from '../../../stores/statistic/statistic.store';

/**
 * Generic chart allows display of
 * - multiple charts
 * - each chart with multiple data series
 * - each series may have a different type (line, column, point, ...)
 * - multiple value axes to display complex data connections
 * https://www.telerik.com/kendo-angular-ui/components/charts/elements/axes
 */
@Component({
  selector: 'ap-generic-chart',
  templateUrl: 'ap-generic-chart.component.html',
  styleUrls: ['ap-generic-chart.component.scss']
})
export class ApGenericChartComponent {
  @Input() chartConfigs: Array<GenericChartConfig> = [];
  @Input() title = '';
  @Input() emptyMessage = '';
  public strokeColor = 'rgba(33, 37, 41, 1)';
  public inactiveColor = 'rgba(33, 37, 41, 0.4)';
  public backgroundColor = '#ffffffbf';
  public isTooltipVisible = false;
  public tooltipX = 0;
  public tooltipY = 0;
  public tooltipText = '';

  constructor(private sanitizer: DomSanitizer,
              private statisticStore: StatisticStore) {
  }

  /**
   * method to retrieve content of the tooltip (if defined)
   * @param dataItem the current bar dataItem
   * @param chart the current chart
   */
  public getTooltip(dataItem: any, chart: GenericChartConfig): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(
      chart?.tooltipDelegate ? chart?.tooltipDelegate(dataItem, chart) : dataItem?.value ?? '');
  }

  /**
   * method to retrieve content of the note (if defined)
   * @param chartConfig the current chart
   */
  public getNote(chartConfig: GenericChartConfig): (p1: any) => string {
    return (item: any) => {
      if (item && chartConfig?.noteDelegate) {
        return chartConfig.noteDelegate(item?.dataItem, chartConfig);
      } else if (item && chartConfig?.noteField !== '') {
        return item?.dataItem[chartConfig?.noteField];
      }
      return '';
    };
  }

  /**
   * method to retrieve label of value axis (y-axis)
   * @param yAxisConfig the current yAxisConfig
   */
  public getLabel(yAxisConfig: GenericYAxisConfig): (p1: any) => string {
    return (item: any) => {
      if (item && yAxisConfig?.labelDelegate) {
        return yAxisConfig.labelDelegate(item?.value);
      }
      return item?.value?.toString() ?? '';
    };
  }

  /**
   * method to retrieve label of category axis (x-axis)
   * @param xAxisConfig the current xAxisConfig
   * @param chartConfig the current chartConfig
   */
  public getXAxisLabel(xAxisConfig: GenericXAxisConfig, chartConfig: GenericChartConfig): (p1: any) => string {
    return (item: any) => {
      if (item && xAxisConfig?.labelDelegate) {
        return xAxisConfig.labelDelegate(item?.text, item?.dataItem, chartConfig);
      }
      return item?.text?.toString() ?? '';
    };
  }

  /**
   * Calculates the chart's height depending on the amount of charts to be displayed.
   */
  public calculateChartHeight(): number {
    // calculate height of the chart. If it is a single chart we are checking if the statistic
    // tab shows anything else (data.length > 1) => Then the chart will use 100% of the remaining space.
    // If only the chart is shown, its height is decreased to 75% for aesthetic reasons :)
    switch (this.chartConfigs?.length) {
      case 1: return this.statisticStore.Listen(s => s.data).getValue()?.length > 1 ? 90 : 75;
      case 2: return 50;
      default: return 33;
    }
  }

  /**
   * Handles click event on a legend item to show/hide series
   * @param event the click eventArgs
   * @param chartConfig the current chart's config
   */
  public onLegendItemClick(event: LegendItemClickEvent, chartConfig: GenericChartConfig): void {
    // Prevent the default behavior (toggling only one series)
    event.preventDefault();

    // In case we have multiple series for the same legend item,
    // we want to disable them all at once
    for (const seriesConfig of chartConfig.seriesConfigs) {
      if (seriesConfig.name === event.text) {
        seriesConfig.visible = !seriesConfig.visible;
      }
    }
  }

  /**
   * EventHandler for seriesHover event which creates a custom tooltip
   * because kendo tooltip has a bug and is not shown (or flashing) in
   * case of scatter-charts or if the chart has visible notes
   * @param e SeriesHover eventArgs
   * @param chartConfig the current chartConfig
   */
  public onSeriesHover(e: SeriesHoverEvent, chartConfig: GenericChartConfig): void {
    if (!chartConfig || !chartConfig.tooltipDelegate) {
      return;
    }
    this.tooltipText = chartConfig?.tooltipDelegate(e.dataItem, chartConfig);
    const chartComponent = e?.sender as ChartComponent;
    const chartElement = chartComponent?.surfaceElement?.nativeElement as HTMLElement;
    const chartBoundingBox = chartElement?.getBoundingClientRect();
    this.tooltipX = e?.originalEvent?.clientX - chartBoundingBox?.left + 15;
    this.tooltipY = e?.originalEvent?.clientY - chartBoundingBox?.top + 15;
    this.isTooltipVisible = true;
  }

  /**
   * Hide tooltip when mouse leaves series
   */
  public onSeriesLeave(): void {
    this.isTooltipVisible = false;
  }
}
